/**
 * VeraPaws listicle landing page — shared interactions.
 * Vanilla JS, scoped per-section via [data-section-id] so multiple instances
 * and Shopify theme-editor hot reloads (shopify:section:load) both work.
 * Content never lives in JS: every state change toggles a class or a CSS
 * custom property on markup Liquid already rendered, so nothing here builds HTML.
 */
(function () {
  'use strict';

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* ---------- Countdown banner: genuine expiry only ----------
     Liquid only decides whether a countdown was configured at all (see
     vp-announcement-bar.liquid). This function is the sole authority on
     whether that configured value is actually usable: it must parse to a
     real date that is still in the future, or the timer stays hidden —
     never a fabricated deadline, never a fallback duration. Once the real
     expiry passes, the timer hides itself and stops; it is never reset to
     a new deadline. */
  function initCountdown(root) {
    if (root.__vpInit) return;
    root.__vpInit = true;
    var expiryRaw = root.getAttribute('data-expiry');
    if (!expiryRaw) return;
    var expiryMs = Date.parse(expiryRaw);
    if (isNaN(expiryMs)) return;
    var display = root.querySelector('[data-vp-countdown-display]');
    if (!display) return;

    function format(totalSec) {
      var days = Math.floor(totalSec / 86400);
      var hh = String(Math.floor((totalSec % 86400) / 3600)).padStart(2, '0');
      var mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
      var ss = String(totalSec % 60).padStart(2, '0');
      return days > 0 ? (days + 'd ' + hh + ':' + mm + ':' + ss) : (hh + ':' + mm + ':' + ss);
    }

    var timer;
    function tick() {
      var remaining = Math.floor((expiryMs - Date.now()) / 1000);
      if (remaining <= 0) {
        if (timer) clearInterval(timer);
        root.hidden = true;
        return;
      }
      display.textContent = format(remaining);
    }

    var initialRemaining = Math.floor((expiryMs - Date.now()) / 1000);
    if (initialRemaining <= 0) return; // already past — never displayed, not even at 00:00:00
    root.hidden = false;
    tick();
    timer = setInterval(tick, 1000);
    root.__vpCleanup = function () { clearInterval(timer); };
  }

  /* ---------- Index-based track carousels (hero gallery, reason-2 gallery) ---------- */
  function initTrackGallery(root) {
    if (root.__vpInit) return;
    root.__vpInit = true;
    var track = root.querySelector('[data-vp-track]');
    var count = parseInt(root.getAttribute('data-count'), 10) || 0;
    if (!track || !count) return;
    var index = 0;

    function apply() {
      track.style.setProperty('--vp-index', index);
      each(root.querySelectorAll('[data-vp-dot]'), function (dot) {
        dot.classList.toggle('is-active', parseInt(dot.getAttribute('data-index'), 10) === index);
      });
      each(root.querySelectorAll('[data-vp-thumb]'), function (thumb) {
        thumb.classList.toggle('is-active', parseInt(thumb.getAttribute('data-index'), 10) === index);
      });
      each(root.querySelectorAll('[data-vp-caption]'), function (cap) {
        cap.classList.toggle('is-active', parseInt(cap.getAttribute('data-index'), 10) === index);
      });
    }

    var prev = root.querySelector('[data-vp-prev]');
    var next = root.querySelector('[data-vp-next]');
    if (prev) prev.addEventListener('click', function () { index = (index - 1 + count) % count; apply(); });
    if (next) next.addEventListener('click', function () { index = (index + 1) % count; apply(); });
    each(root.querySelectorAll('[data-vp-thumb]'), function (thumb) {
      thumb.addEventListener('click', function () {
        index = parseInt(thumb.getAttribute('data-index'), 10) || 0;
        apply();
      });
    });
    apply();
  }

  /* ---------- Single-open accordion (ingredients) ---------- */
  function initAccordion(root) {
    if (root.__vpInit) return;
    root.__vpInit = true;
    var items = root.querySelectorAll('[data-vp-accordion-item]');
    each(items, function (item) {
      var trigger = item.querySelector('[data-vp-accordion-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        each(items, function (i) { i.classList.remove('is-open'); i.querySelector('[data-vp-accordion-trigger]').setAttribute('aria-expanded', 'false'); });
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ---------- Testimonial carousel: desktop transform; mobile ignores this and
     scrolls natively (overflow-x + scroll-snap), handled entirely by CSS ---------- */
  function initTestimonialCarousel(root) {
    if (root.__vpInit) return;
    root.__vpInit = true;
    var track = root.querySelector('[data-vp-track]');
    var maxIndex = parseInt(root.getAttribute('data-max-index'), 10) || 0;
    if (!track) return;
    var index = 0;

    function apply() {
      track.style.setProperty('--vp-index', index);
    }
    var prev = root.querySelector('[data-vp-prev]');
    var next = root.querySelector('[data-vp-next]');
    if (prev) prev.addEventListener('click', function () { index = Math.max(0, index - 1); apply(); });
    if (next) next.addEventListener('click', function () { index = Math.min(maxIndex, index + 1); apply(); });
    apply();
  }

  /* ---------- Sticky bottom bar ----------
     Navigation aid only: its button is a plain link (see vp-sticky-bar.liquid),
     never a cart/checkout action. While visible, reserves exactly its own
     measured height as body clearance so it can never permanently cover
     page content (e.g. the last accordion row, or the footer) — see the
     body.vp-has-sticky-bar rule in vp-base.css. */
  function initStickyBar(root) {
    if (root.__vpInit) return;
    root.__vpInit = true;
    var offerId = root.getAttribute('data-observe-id') || 'vp-final-bridge';
    var offerEl = document.getElementById(offerId);
    var scrolledPast = false;
    var nearOffer = false;

    function applyClearance(visible) {
      document.body.classList.toggle('vp-has-sticky-bar', visible);
      if (visible) {
        document.body.style.setProperty('--vp-sticky-bar-h', root.offsetHeight + 'px');
      }
    }
    function update() {
      var visible = scrolledPast && !nearOffer;
      root.classList.toggle('is-visible', visible);
      applyClearance(visible);
    }
    var onScroll = function () {
      var show = window.scrollY > 700;
      if (show !== scrolledPast) { scrolledPast = show; update(); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      if (root.classList.contains('is-visible')) {
        document.body.style.setProperty('--vp-sticky-bar-h', root.offsetHeight + 'px');
      }
    }, { passive: true });
    onScroll();

    if (offerEl && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        nearOffer = entries[0].isIntersecting;
        update();
      }, { threshold: 0.15 });
      io.observe(offerEl);
    }
  }

  /* ---------- Boot ---------- */
  function initAll(context) {
    each(context.querySelectorAll('[data-vp-countdown]'), initCountdown);
    each(context.querySelectorAll('[data-vp-track-gallery]'), initTrackGallery);
    each(context.querySelectorAll('[data-vp-accordion]'), initAccordion);
    each(context.querySelectorAll('[data-vp-testimonial-carousel]'), initTestimonialCarousel);
    each(context.querySelectorAll('[data-vp-sticky-bar]'), initStickyBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
